// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { mergeWithCss, Roboto } from "@mcmc/XDL/Utilities";
import mergeToNew from "@mcmc/XDL/Utilities/xMergeToNew";
import React from "react";
import styled, { css } from "styled-components";

interface Props {
    categories: AutocompleteItemModel[] | null;
    popularCategories?: AutocompleteItemModel[] | null;
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    isEvents: boolean;
    selectedCategoryId?: string | null;
    loadCategoryProducts: (category: AutocompleteItemModel, isEvents: boolean) => void;
    unloadCategoryProducts: () => void;
    trainingListPageUrl?: string;
    categoryListPageUrl?: string;
    extendedStyles?: AutocompleteCategoriesStyles;
    isRetailSearchCompletionResults?: boolean;
}

export const enum autocompleteItemStyleTypes {
    focused = "focused",
    normal = "normal",
}

export type AutocompleteItemStyleType = autocompleteItemStyleTypes.focused | autocompleteItemStyleTypes.normal;

export interface AutocompleteCategoriesStyles {
    wrapper?: InjectableCss;
    heading?: InjectableCss;
    headerText?: TypographyPresentationProps;
    subHeaderText?: TypographyPresentationProps;
    divider?: InjectableCss;
    headerLink?: LinkPresentationProps;
    autocompleteCategoriesRowWrapper?: InjectableCss;
    autocompleteCategoriesColumnWrapper?: InjectableCss;
    item: {
        [autocompleteItemStyleTypes.normal]: InjectableCss;
        [autocompleteItemStyleTypes.focused]: InjectableCss;
    };
    link: {
        [autocompleteItemStyleTypes.normal]: LinkPresentationProps;
        [autocompleteItemStyleTypes.focused]: LinkPresentationProps;
    };
    linkSubTitle?: TypographyPresentationProps;
    getStyleType: (
        item: AutocompleteItemModel,
        focusedItem?: AutocompleteItemModel,
        selectedId?: string | null,
    ) => AutocompleteItemStyleType;
}

const linkStyle: LinkPresentationProps = {
    color: "text",
    hoverStyle: {
        color: "text.main",
        textDecoration: "underline",
    },
    typographyProps: {
        ellipsis: true,
        ...Roboto,
        color: "text",
        size: 16,
        lineHeight: "28px",
        weight: "normal",
    },
    css: css`
        ${getProp("theme.link.defaultProps.css")}
        max-width: 100%;
    `,
};

const itemStyle: InjectableCss = {
    css: css`
        display: block;
        text-align: left;
        margin: 0 -10px;
        padding: 0 10px;
        transition: background-color 0.2s ease-in-out;
    `,
};

export const autocompleteCategoriesStyles: AutocompleteCategoriesStyles = {
    wrapper: {
        css: css`
            flex: 1 1 100%;
            padding-bottom: 15px;
            border-bottom: 1px solid ${getColor("common.border")};
            margin-bottom: 15px;

            &:last-child {
                margin-bottom: 0;
                padding-bottom: 0;
                border-bottom: none;
            }
        `,
    },
    heading: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            padding: 10px 0 5px 0;
        `,
    },
    headerText: {
        variant: "h6",
        weight: 700,
        css: css`
            text-align: left;
            letter-spacing: 0;
            margin-bottom: 0;
        `,
    },
    headerLink: {
        typographyProps: {
            ...Roboto,
            css: css`
                margin-left: 10px;
            `,
        },
    },
    item: {
        [autocompleteItemStyleTypes.normal]: itemStyle,
        [autocompleteItemStyleTypes.focused]: mergeWithCss(itemStyle, {
            css: css`
                background-color: ${getColor("common.ui4")};
            `,
        }),
    },
    link: {
        [autocompleteItemStyleTypes.normal]: linkStyle,
        [autocompleteItemStyleTypes.focused]: mergeWithCss(linkStyle, {
            color: "primary",
            hoverStyle: {
                color: "primary",
            },
            typographyProps: {
                color: "primary",
            },
        }),
    },
    linkSubTitle: {
        css: css`
            text-decoration: none;
        `,
    },
    getStyleType: (
        item: AutocompleteItemModel,
        focusedItem?: AutocompleteItemModel,
        selectedId?: string | null,
    ): AutocompleteItemStyleType => {
        return focusedItem === item || selectedId === item.id
            ? autocompleteItemStyleTypes.focused
            : autocompleteItemStyleTypes.normal;
    },
};

const styles = mergeWithCss(autocompleteCategoriesStyles, {
    item: {
        [autocompleteItemStyleTypes.normal]: {
            css: css`
                cursor: pointer;

                &:hover {
                    background-color: ${getColor("common.ui4")};
                }
            `,
        },
        [autocompleteItemStyleTypes.focused]: {
            css: css`
                cursor: pointer;
            `,
        },
    },
});

const Divider = styled.hr`
    ${styles.divider?.css}
`;

class AutocompleteCategories extends React.Component<Props> {
    private readonly styles: AutocompleteCategoriesStyles;
    private touchStart = false;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { categories, isEvents, trainingListPageUrl, categoryListPageUrl, focusedItem, selectedCategoryId } =
            this.props;
        if (!categories || categories.length === 0) {
            return null;
        }

        const styles = this.styles;

        return (
            <StyledWrapper
                {...styles.wrapper}
                data-test-selector="autocompleteCategories_Header"
                className="autocomplete-categories-header"
            >
                {!isEvents && (
                    <StyledWrapper {...styles.heading}>
                        <Typography {...styles.headerText}>{translate("Product Categories")}</Typography>
                        <Link
                            href={categoryListPageUrl}
                            {...styles.headerLink}
                            aria-label={translate("View All Product Categories")}
                        >
                            {translate("View All")}
                        </Link>
                    </StyledWrapper>
                )}
                {isEvents && (
                    <StyledWrapper {...styles.heading}>
                        <Typography {...styles.headerText}>{translate("Training")}</Typography>
                        <Link
                            href={trainingListPageUrl}
                            {...styles.headerLink}
                            aria-label={translate("View All Trainings")}
                        >
                            {translate("View All")}
                        </Link>
                    </StyledWrapper>
                )}
                {categories.map(category => (
                    <StyledWrapper
                        key={`${category.id}`}
                        {...styles.item[styles.getStyleType(category, focusedItem, selectedCategoryId)]}
                        onMouseEnter={(e: React.MouseEvent) => {
                            if (!isEvents) {
                                this.props.loadCategoryProducts(category, isEvents);
                            }
                        }}
                        onMouseLeave={(e: React.MouseEvent) => {
                            if (!isEvents) {
                                this.props.unloadCategoryProducts();
                            }
                        }}
                    >
                        <Link
                            onTouchStart={(e: React.TouchEvent) => {
                                if (!isEvents) {
                                    e.preventDefault();
                                    this.touchStart = true;
                                }
                            }}
                            onClick={(e: React.MouseEvent) => {
                                e.preventDefault();
                                if (!this.touchStart) {
                                    this.props.goToUrl(category.url);
                                }
                                this.touchStart = false;
                            }}
                            {...styles.link[styles.getStyleType(category, focusedItem, selectedCategoryId)]}
                            className="autocomplete-categories-item-link"
                        >
                            {category.displayTitle}
                            {category.displaySubtitle && (
                                <Typography {...styles.linkSubTitle}>
                                    {` ${translate("in")} `} {category.displaySubtitle}
                                </Typography>
                            )}
                        </Link>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        );
    }
}

export default AutocompleteCategories;
